import { AiUsageRecordRow, AiUsageRecordsResponse } from "../types/MeGenTypes";

export const AI_USAGE_PAGE_SIZE = 2000;
export type AiUsageRange = { startDate: string; endDate: string; startIso: string; endIso: string; timezone: string };
export type AiUsageProgress = { loaded: number; total: number | null };

const localIso = (date: Date): string => {
  const offset = -date.getTimezoneOffset();
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}.${String(date.getMilliseconds()).padStart(3, "0")}${offset < 0 ? "-" : "+"}${pad(Math.floor(Math.abs(offset) / 60))}:${pad(Math.abs(offset) % 60)}`;
};

// Match dayOf/toLocalDay: the dashboard uses the browser's local calendar.
export const aiUsageRangeOf = (startDate: string, endDate: string, now = new Date()): AiUsageRange => {
  const parse = (value: string) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) throw new Error("Invalid date range");
    const [year, month, day] = value.split("-").map(Number);
    const date = new Date(year, month - 1, day);
    if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) throw new Error("Invalid date range");
    return date;
  };
  const start = parse(startDate);
  const end = parse(endDate);
  if (start > end) throw new Error("Invalid date range");
  end.setHours(23, 59, 59, 999);
  const endIso = now <= end
    ? localIso(now) // Freeze today's/future upper bound before the first page, including whole-year ranges.
    : localIso(end).replace(".999", ".999999");
  return { startDate, endDate, startIso: localIso(start), endIso, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone };
};

export class AiUsageLoadError extends Error {
  constructor(public reason: "cancelled" | "inconsistent" | "request", message: string) { super(message); }
}

export const loadAllAiUsageRecords = async (
  fetchPage: (limit: number, offset: number) => Promise<AiUsageRecordsResponse>,
  isCurrent: () => boolean,
  onProgress: (progress: AiUsageProgress) => void
): Promise<AiUsageRecordRow[]> => {
  const records = new Map<number, AiUsageRecordRow>();
  let expectedTotal: number | null = null;
  let offset = 0;
  let lastTimestamp = Infinity;
  while (true) {
    if (!isCurrent()) throw new AiUsageLoadError("cancelled", "Usage loading cancelled");
    const page = await fetchPage(AI_USAGE_PAGE_SIZE, offset);
    if (!isCurrent()) throw new AiUsageLoadError("cancelled", "Usage loading cancelled");
    if (!Number.isSafeInteger(page.total) || page.total < 0 || (expectedTotal !== null && expectedTotal !== page.total)) throw new AiUsageLoadError("inconsistent", "Usage record count changed while loading");
    expectedTotal = page.total;
    if (page.offset !== offset || page.records.length > AI_USAGE_PAGE_SIZE) throw new AiUsageLoadError("inconsistent", "Unexpected usage page");
    for (const record of page.records) {
      if (typeof record.id !== "number" || !Number.isSafeInteger(record.id) || record.id < 0) throw new AiUsageLoadError("inconsistent", "Invalid usage record id");
      if (records.has(record.id)) throw new AiUsageLoadError("inconsistent", "Duplicate usage record across pages");
      if (record.created_at) {
        const timestamp = new Date(record.created_at).getTime();
        if (!Number.isFinite(timestamp) || timestamp > lastTimestamp) throw new AiUsageLoadError("inconsistent", "Usage records are not ordered newest first");
        lastTimestamp = timestamp;
      }
      records.set(record.id, record);
    }
    onProgress({ loaded: records.size, total: expectedTotal });
    if (records.size > expectedTotal) throw new AiUsageLoadError("inconsistent", "Usage record count exceeded total");
    if (records.size === expectedTotal) return Array.from(records.values());
    if (page.records.length === 0) throw new AiUsageLoadError("inconsistent", "Usage page ended before total was reached");
    offset += page.records.length;
  }
};
